// ── PRF ON THE WEATHER PAGE: each insured interval against its trigger ─────
//
// One question per interval, the one a PRF customer asks in August: "is my grid
// under the line?" The answer is the grid's rainfall index (percent of normal)
// set against the trigger, which is the coverage level (90% coverage triggers at
// an index below 90). Nothing here prices anything: no liability, no premium, no
// indemnity, because the Weather page carries no dollar figures at all.
//
// The rules this follows are the PRF card's own (PRFView), kept identical so the
// two pages can never disagree about one interval:
//   • FINAL is RMA's settled index and is shown as it stands.
//   • While an interval is OPEN there is no index and no trigger call. The only
//     number is percent of normal TO DATE: rain so far ÷ the normal for the same
//     days (src/prf/prfIntervalStatus.js, the rule every PRF screen shares).
//     Rain so far ÷ the WHOLE interval's normal reads low by however much of the
//     interval is still to come, and called open intervals "below trigger" that
//     were not (Kevin, 2026-11-02: "% of normal to date within that interval").
//   • A CLOSED interval with an estimate that covers its last day is judged on
//     that estimate until RMA settles it. One whose rain total stops short of its
//     last day still reads to date, unjudged.
//   • An interval that has not started is UPCOMING, and has no index to show.
//   • The settled inches go through the same contradiction guard as everywhere
//     else (prfFinalInches, passed in), so a unit defect upstream cannot print.
//
// PRF has eleven intervals, JANFEB through NOVDEC, all inside the crop year.
// There is no DECJAN in the Rainfall Index (api/_prf-index.js), so a code this
// module does not know is reported as such rather than timed.
//
// Pure CommonJS like wxSeason.js, so tools/check-wx-prf.js runs it under node.

const ORDER = ["JANFEB", "FEBMAR", "MARAPR", "APRMAY", "MAYJUN", "JUNJUL",
  "JULAUG", "AUGSEP", "SEPOCT", "OCTNOV", "NOVDEC"];
const LABEL = { JANFEB: "Jan–Feb", FEBMAR: "Feb–Mar", MARAPR: "Mar–Apr", APRMAY: "Apr–May",
  MAYJUN: "May–Jun", JUNJUL: "Jun–Jul", JULAUG: "Jul–Aug", AUGSEP: "Aug–Sep",
  SEPOCT: "Sep–Oct", OCTNOV: "Oct–Nov", NOVDEC: "Nov–Dec" };
// Kept for callers that read it; no reading is judged while its interval is open.
const TRIGGER_MIN_ELAPSED = 0.5;
const PRF_IS = require("../prf/prfIntervalStatus");

const num = (v) => (v == null || v === "" ? null : (Number.isFinite(Number(v)) ? Number(v) : null));

// UTC bounds of one interval in one crop year: first day of its first month to
// the last instant of its second month. null for a code PRF does not have.
function intervalBounds(code, year) {
  const i = ORDER.indexOf(code);
  const y = Number(year);
  if (i < 0 || !Number.isInteger(y)) return null;
  return { start: Date.UTC(y, i, 1), end: Date.UTC(y, i + 2, 0, 23, 59, 59) };
}

// Fraction of the interval that has passed at nowMs, 0..1; null for an unknown code.
function elapsed(code, year, nowMs) {
  const b = intervalBounds(code, year);
  if (!b) return null;
  if (nowMs < b.start) return 0;
  if (nowMs >= b.end) return 1;
  return (nowMs - b.start) / (b.end - b.start);
}

// One interval's reading. `entry` is the prf_grid_indices row for (grid, code, year).
// state: final | estimate | sofar | upcoming | nodata | unknown
//   estimate the interval has closed and the estimate covers its last day: judged
//   sofar    open (or closed with rain short of its last day): percent of normal to
//            date in `toDate`, no index, never judged against the trigger
//   nodata   the interval has started (or closed) and nothing usable is stored
function readInterval({ code, entry, coverageLevel, year, nowMs, finalInches }) {
  const e = elapsed(code, year, nowMs);
  const trigger = num(coverageLevel) != null ? Math.round(num(coverageLevel) * 100) : null;
  const out = { code, label: LABEL[code] || code, trigger, elapsed: e,
    state: "nodata", index: null, toDate: null, inches: null, normal: null, normalToDate: null, below: null, asOf: null };
  if (e == null) { out.state = "unknown"; return out; }
  const r = entry || null;
  out.normal = r ? num(r.normal_rainfall_inches) : null;
  if (r && r.is_final && num(r.final_index_value) != null) {
    out.state = "final";
    out.index = num(r.final_index_value);
    out.inches = num(finalInches(r.final_rainfall_inches, r.final_index_value, r.normal_rainfall_inches));
    out.below = trigger != null ? out.index < trigger : null;
    return out;
  }
  if (e === 0) { out.state = "upcoming"; return out; }
  if (!r) return out;
  const reading = PRF_IS.intervalReading(code, year, {
    finalValue: null,
    estimatedValue: r.estimated_index_value,
    estimatedInches: r.estimated_rainfall_inches,
    normalInches: r.normal_rainfall_inches,
    normalToDateInches: r.normal_to_date_inches,
    estimatedAsOf: r.estimated_as_of,
  }, { now: nowMs });
  out.inches = num(r.estimated_rainfall_inches);
  out.asOf = r.estimated_as_of || null;
  if (reading.source === "ESTIMATED" && reading.index != null) {
    out.state = "estimate";
    out.index = reading.index;
    out.below = trigger != null ? out.index < trigger : null;
  } else if (reading.source === "TO_DATE" && reading.toDate != null) {
    out.state = "sofar";
    out.toDate = reading.toDate;
    out.normalToDate = PRF_IS.normalToDate(code, year, { normalInches: r.normal_rainfall_inches,
      normalToDateInches: r.normal_to_date_inches, asOf: r.estimated_as_of });
  }
  return out;
}

// The farm's current-year PRF, grouped by grid + county + coverage level, each
// group listing the intervals it insures with their acres and reading.
//   policies     prfCoverageState(...).policies (ONE crop year, already narrowed)
//   indexRows    prf_grid_indices rows for those grids and that year
//   parseCode    App's parsePRFCode (numeric and suffixed codes → JANFEB etc.)
// Acres for one interval in one group are summed over its unit lines, which is
// correct inside one crop year (the PRF card's Kingsley rule).
function prfPlan({ policies, indexRows, year, nowMs, parseCode, finalInches }) {
  const byKey = new Map();
  for (const r of indexRows || []) byKey.set(`${r.grid_id}|${r.interval_code}`, r);
  const groups = new Map();
  for (const p of policies || []) {
    const cl = num(p && p.coverageLevel);
    for (const iv of (p && p.intervals) || []) {
      const grid = iv.gridId != null ? String(iv.gridId) : (iv.grid_id != null ? String(iv.grid_id) : "");
      if (!grid) continue;
      const code = parseCode(iv.intervalCode) || String(iv.intervalCode || "");
      const county = String(iv.county || p.county || "");
      const gk = `${grid}|${county.toUpperCase()}|${cl == null ? "" : cl}`;
      if (!groups.has(gk)) groups.set(gk, { grid, county, coverageLevel: cl, ivs: new Map() });
      const g = groups.get(gk);
      if (!g.ivs.has(code)) g.ivs.set(code, { code, acres: 0 });
      g.ivs.get(code).acres += num(iv.insuredAcres) || 0;
    }
  }
  const rank = (c) => { const i = ORDER.indexOf(c); return i < 0 ? 99 : i; };
  const out = [];
  for (const g of groups.values()) {
    const intervals = [...g.ivs.values()]
      .sort((a, b) => rank(a.code) - rank(b.code) || a.code.localeCompare(b.code))
      .map((iv) => ({ ...readInterval({ code: iv.code, entry: byKey.get(`${g.grid}|${iv.code}`),
        coverageLevel: g.coverageLevel, year, nowMs, finalInches }), acres: iv.acres }));
    out.push({ grid: g.grid, county: g.county, coverageLevel: g.coverageLevel,
      trigger: g.coverageLevel != null ? Math.round(g.coverageLevel * 100) : null,
      acres: Math.max(0, ...intervals.map((i) => i.acres)), intervals,
      belowCount: intervals.filter((i) => i.below === true).length });
  }
  return out.sort((a, b) => b.acres - a.acres || a.grid.localeCompare(b.grid));
}

// The interval's rain as a percent of its normal, from its own inches, and whether
// that agrees with the index shown beside it. The index IS this percent, so the two
// must match; inches stored to two decimals put the honest gap well under a point.
// A row where they part is flagged rather than quietly shown.
const PCT_AGREE = 1;
function pctOfNormal(iv) {
  const inches = iv ? num(iv.inches) : null;
  const normal = iv ? num(iv.normal) : null;
  if (inches == null || normal == null || !(normal > 0)) return { pct: null, agrees: null };
  const pct = (inches / normal) * 100;
  const idx = iv.index != null ? num(iv.index) : null;
  return { pct, agrees: idx == null ? null : Math.abs(pct - idx) <= PCT_AGREE };
}

module.exports = { ORDER, LABEL, TRIGGER_MIN_ELAPSED, intervalBounds, elapsed, readInterval, prfPlan, PCT_AGREE, pctOfNormal };
